/*
 * Author: zoesvision.com
 * Purpose: Isolated Embla lifecycle, controls, media, and editor integration for z-carousel.
 */

if (!customElements.get('z-carousel')) {
  customElements.define('z-carousel', class ZCarousel extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.viewport = this.querySelector('[data-z-viewport]');
      this.items = Array.from(this.querySelectorAll('[data-z-carousel-item]'));
      if (!this.viewport || !this.items.length) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      // zoesvision.com | 2026-07-29 | Purpose: Resolve independent desktop and mobile carousel alignment.
      // zoesvision.com | 2026-08-15 | Purpose: Switch carousel desktop behavior at the unified 990px breakpoint.
      this.desktopLayout = matchMedia('(min-width: 990px)');
      this.previousButton = this.querySelector('[data-z-previous]');
      this.nextButton = this.querySelector('[data-z-next]');
      this.navigation = this.querySelector('[data-z-navigation]');
      this.dotsContainer = this.querySelector('[data-z-dots]');
      this.liveRegion = this.querySelector('[data-z-live-region]');

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleLayoutChange = this.handleLayoutChange.bind(this);
      this.bindNativeEvents();
      // zoesvision.com | 2026-08-15 | Purpose: Keep opt-in side controls centered on responsive item media.
      this.initializeNavigationPositionObserver();
      this.initializeEmbla();
    }

    disconnectedCallback() {
      this.destroy();
    }

    bindNativeEvents() {
      const signal = this.abortController.signal;
      this.addEventListener('keydown', this.handleKeydown, { signal });
      this.previousButton?.addEventListener('click', () => this.navigate('previous'), { signal });
      this.nextButton?.addEventListener('click', () => this.navigate('next'), { signal });
      document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
      this.reducedMotion.addEventListener('change', this.handleMotionChange, { signal });
      this.desktopLayout.addEventListener('change', this.handleLayoutChange, { signal });
    }

    initializeEmbla() {
      if (typeof globalThis.EmblaCarousel !== 'function') {
        this.dependencyAttempts = (this.dependencyAttempts || 0) + 1;
        if (this.dependencyAttempts <= 120 && this.isConnected) {
          this.dependencyFrame = requestAnimationFrame(() => this.initializeEmbla());
        } else {
          this.dataset.zReady = 'true';
        }
        return;
      }

      const plugins = [];
      if (
        this.dataset.zAutoplay === 'true'
        && this.items.length > 1
        && typeof globalThis.EmblaCarouselAutoplay === 'function'
      ) {
        plugins.push(globalThis.EmblaCarouselAutoplay({
          delay: Number(this.dataset.zAutoplayDelay) || 6000,
          playOnInit: !this.reducedMotion.matches,
          stopOnInteraction: false,
          stopOnMouseEnter: this.dataset.zPauseOnHover === 'true',
          stopOnFocusIn: true
        }));
      }

      this.embla = globalThis.EmblaCarousel(this.viewport, {
        loop: this.dataset.zLoop === 'true',
        align: this.getAlignment(),
        containScroll: 'trimSnaps',
        slidesToScroll: Number(this.dataset.zSlidesPerScroll) || 1,
        inViewThreshold: 0.35,
        watchDrag: this.items.length > 1
      }, plugins);

      this.handleSelect = this.handleSelect.bind(this);
      this.handleSlidesInView = this.handleSlidesInView.bind(this);
      this.handleReInit = this.handleReInit.bind(this);
      this.embla.on('select', this.handleSelect);
      this.embla.on('slidesInView', this.handleSlidesInView);
      this.embla.on('reInit', this.handleReInit);

      this.buildDots();
      this.syncState();
      this.scheduleNavigationPosition();
      this.dataset.zReady = 'true';
    }

    getAlignment() {
      const alignment = this.desktopLayout.matches
        ? this.dataset.zDesktopAlignment
        : this.dataset.zMobileAlignment;
      return alignment === 'center' ? 'center' : 'start';
    }

    buildDots() {
      if (!this.dotsContainer || !this.embla) return;

      this.dotsContainer.replaceChildren();
      this.dots = this.embla.scrollSnapList().map((unusedSnap, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'z-carousel__dot';
        button.setAttribute(
          'aria-label',
          this.formatLabel(this.dataset.zLabelGoTo, index + 1, this.items.length)
        );
        button.addEventListener('click', () => {
          this.announceNextSelection = true;
          this.embla?.scrollTo(index);
          this.embla?.plugins()?.autoplay?.reset();
        }, { signal: this.abortController.signal });
        this.dotsContainer.append(button);
        return button;
      });
    }

    navigate(direction) {
      if (!this.embla) return;
      this.announceNextSelection = true;
      direction === 'previous' ? this.embla.scrollPrev() : this.embla.scrollNext();
      this.embla.plugins()?.autoplay?.reset();
    }

    handleSelect() {
      this.syncState();
      this.scheduleNavigationPosition();
      if (this.announceNextSelection) this.announceSelection();
      this.announceNextSelection = false;
    }

    handleSlidesInView() {
      this.syncItemsInView();
      this.syncVideos();
      this.syncMediaActivation();
    }

    handleReInit() {
      this.items = Array.from(this.querySelectorAll('[data-z-carousel-item]'));
      this.observeNavigationPositionTargets();
      this.buildDots();
      this.syncState();
      this.scheduleNavigationPosition();
    }

    syncState() {
      if (!this.embla) return;

      const selectedIndex = this.embla.selectedScrollSnap();
      this.dots?.forEach((dot, index) => {
        if (index === selectedIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.previousButton) this.previousButton.disabled = !this.embla.canScrollPrev();
      if (this.nextButton) this.nextButton.disabled = !this.embla.canScrollNext();
      this.syncItemsInView();
      this.syncVideos();
      this.syncMediaActivation(selectedIndex);
    }

    syncItemsInView() {
      if (!this.embla) return;
      const visible = new Set(this.embla.slidesInView());

      this.items.forEach((item, index) => {
        const isVisible = visible.has(index);
        item.setAttribute(
          'aria-label',
          this.formatLabel(this.dataset.zLabelStatus, index + 1, this.items.length)
        );
        item.setAttribute('aria-hidden', String(!isVisible));
        this.setItemFocusable(item, isVisible);
      });
    }

    setItemFocusable(item, isVisible) {
      item.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        if (!isVisible && element.tabIndex !== -1) {
          element.dataset.zCarouselTabindex = element.getAttribute('tabindex') ?? '';
          element.tabIndex = -1;
        } else if (isVisible && Object.hasOwn(element.dataset, 'zCarouselTabindex')) {
          const previous = element.dataset.zCarouselTabindex;
          if (previous === '') element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', previous);
          delete element.dataset.zCarouselTabindex;
        }
      });
    }

    syncVideos() {
      const visible = new Set(this.embla?.slidesInView() || []);
      const canPlay = document.visibilityState === 'visible' && !this.reducedMotion.matches;
      // zoesvision.com | 2026-08-19 | Purpose: Warm visible and next carousel videos without changing which cards play.
      const selectedIndex = this.embla?.selectedScrollSnap() || 0;
      const nextIndex = selectedIndex + 1 < this.items.length
        ? selectedIndex + 1
        : this.dataset.zLoop === 'true' ? 0 : selectedIndex;

      this.items.forEach((item, index) => {
        /* zoesvision.com | 2026-08-16 | Purpose: Leave nested z-media playback to its component while preserving carousel-item media behavior. */
        item.querySelectorAll('.z-carousel-item__video').forEach((video) => {
          video.preload = visible.has(index) || index === nextIndex ? 'metadata' : 'none';
          if (canPlay && visible.has(index)) video.play().catch(() => {});
          else video.pause();
        });
      });
    }

    syncMediaActivation(selectedIndex = this.embla?.selectedScrollSnap() || 0) {
      /* zoesvision.com | 2026-08-16 | Purpose: Activate nested z-media only for the selected logical carousel item. */
      // zoesvision.com | 2026-08-19 | Purpose: Prewarm nested media in the next logical item without activating playback.
      const nextIndex = selectedIndex + 1 < this.items.length
        ? selectedIndex + 1
        : this.dataset.zLoop === 'true' ? 0 : selectedIndex;
      this.items.forEach((item, index) => {
        item.querySelectorAll('z-media').forEach((media) => {
          if (media.closest('z-carousel') !== this) return;
          media.dispatchEvent(new CustomEvent('z:media-carousel-active', {
            detail: { active: index === selectedIndex, preload: index === selectedIndex || index === nextIndex }
          }));
        });
      });
    }

    releaseMediaActivation() {
      this.querySelectorAll('z-media').forEach((media) => {
        if (media.closest('z-carousel') !== this) return;
        media.dispatchEvent(new CustomEvent('z:media-carousel-release'));
      });
    }

    announceSelection() {
      if (!this.liveRegion || !this.embla) return;
      this.liveRegion.textContent = this.formatLabel(
        this.dataset.zLabelStatus,
        this.embla.selectedScrollSnap() + 1,
        this.items.length
      );
    }

    handleKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.matches('input, select, textarea')) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.navigate(event.key === 'ArrowLeft' ? 'previous' : 'next');
    }

    handleBlockSelect(event) {
      const selectedItem = this.items.find((item) => item.dataset.zBlockId === event.detail?.blockId);
      if (!selectedItem || !this.embla) return;

      this.embla.plugins()?.autoplay?.stop();
      this.embla.scrollTo(this.items.indexOf(selectedItem), true);
      selectedItem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    handleVisibilityChange() {
      const autoplay = this.embla?.plugins()?.autoplay;
      if (document.visibilityState === 'hidden') autoplay?.stop();
      else if (!this.reducedMotion.matches && this.dataset.zAutoplay === 'true') autoplay?.play();
      this.syncVideos();
    }

    handleMotionChange() {
      const autoplay = this.embla?.plugins()?.autoplay;
      if (this.reducedMotion.matches) autoplay?.stop();
      else if (document.visibilityState === 'visible' && this.dataset.zAutoplay === 'true') autoplay?.play();
      this.syncVideos();
    }

    handleLayoutChange() {
      this.embla?.reInit({ align: this.getAlignment() });
      this.scheduleNavigationPosition();
    }

    initializeNavigationPositionObserver() {
      if (
        this.dataset.zNavigationVerticalPosition !== 'image_center'
        || !this.navigation
        || typeof ResizeObserver !== 'function'
      ) return;

      this.navigationPositionObserver = new ResizeObserver(() => {
        this.scheduleNavigationPosition();
      });
      this.observeNavigationPositionTargets();
    }

    observeNavigationPositionTargets() {
      if (!this.navigationPositionObserver) return;

      this.navigationPositionObserver.disconnect();
      this.navigationPositionObserver.observe(this.viewport);
    }

    scheduleNavigationPosition() {
      if (
        this.dataset.zNavigationVerticalPosition !== 'image_center'
        || !this.navigation
      ) return;

      cancelAnimationFrame(this.navigationPositionFrame);
      this.navigationPositionFrame = requestAnimationFrame(() => this.updateNavigationPosition());
    }

    updateNavigationPosition() {
      if (!this.desktopLayout.matches) {
        delete this.dataset.zNavigationCenterReady;
        this.style.removeProperty('--z-carousel-nav-center');
        return;
      }

      const viewportRect = this.viewport.getBoundingClientRect();
      if (!viewportRect.height) return;

      const configuredPercent = Number(this.dataset.zNavigationVerticalPercent);
      const percent = Number.isFinite(configuredPercent)
        ? Math.min(100, Math.max(0, configuredPercent))
        : 50;
      // zoesvision.com | 2026-08-17 | Purpose: Measure from the card-row grid area without adding the header offset twice.
      const position = viewportRect.height * percent / 100;

      this.style.setProperty('--z-carousel-nav-center', `${position}px`);
      this.dataset.zNavigationCenterReady = 'true';
    }

    formatLabel(template, index, total) {
      return (template || `${index} / ${total}`)
        .replace('[index]', String(index))
        .replace('[total]', String(total));
    }

    destroy() {
      cancelAnimationFrame(this.dependencyFrame);
      cancelAnimationFrame(this.navigationPositionFrame);
      this.abortController?.abort();
      this.navigationPositionObserver?.disconnect();
      this.releaseMediaActivation();
      this.items?.forEach((item) => {
        this.setItemFocusable(item, true);
        item.removeAttribute('aria-hidden');
        item.querySelectorAll('.z-carousel-item__video').forEach((video) => video.pause());
      });
      this.embla?.destroy();
      this.dotsContainer?.replaceChildren();
      this.embla = null;
      this.navigationPositionObserver = null;
      this.style.removeProperty('--z-carousel-nav-center');
      delete this.dataset.zNavigationCenterReady;
      this.isInitialized = false;
      this.dataset.zReady = 'false';
    }
  });
}
